import { z } from "zod";
import { type Author } from "../../firestore/cms/author";
import { type BlogArticle } from "../../firestore/cms/blogArticle";

// Define a Zod schema for validating blog article requests
export const BlogArticleServerSchema = z.object({
  slug: z.string().min(1),
});

// Create a type from the Zod schema
export type BlogArticleServerQueryParams = z.infer<typeof BlogArticleServerSchema>;

// Define a type for the transformed article with millisecond timestamps and image URL
export type BlogArticleServerWithImageUrl = Omit<BlogArticle, "date" | "created_on" | "updated_on"> & {
  imageUrl: string;
  dateMillis: number;
  createdOnMillis: number;
  updatedOnMillis: number;
};

// The response type for the getBlogArticle endpoint
export interface BlogArticleServerResponse {
  article: BlogArticleServerWithImageUrl;
  author: Author;
}

export const BlogArticlesServerSchema = z.object({
  limit: z
    .string()
    .refine(
      (val) => {
        const num = Number(val);
        return Number.isInteger(num) && num >= 1 && num <= 10;
      },
      {
        message: "limit must be a string representing an integer between 1 and 10",
      },
    )
    .optional(),
  startAfterDateTimestamp: z.string().optional(),
});

// Use z.infer to create a type that matches the Zod schema
export type BlogArticlesServerQueryParams = z.infer<typeof BlogArticlesServerSchema>;

export type BlogArticleServer = Omit<BlogArticle, "date" | "created_on" | "updated_on"> & {
  dateMillis: number;
  createdOnMillis: number;
  updatedOnMillis: number;
};

export interface BlogArticlesServerResponse {
  articles: BlogArticleServer[];
  pagination: {
    hasMore: boolean;
    startAfterDateTimestamp?: string;
  };
}
